/**
 * バンドル商品の管理
 * shopping-bag, message-card, wrapping の3つの商品をハンドルベースで管理
 */
class BundleProducts {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // チェックボックスの変更イベント
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('f-product__bundle-add-checkbox')) {
        this.handleCheckboxChange(e.target);
      }
    });

    // バリアント選択の変更イベント
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('f-product__bundle-variant-select')) {
        this.handleVariantChange(e.target);
      }
    });

    // 数量入力の変更イベント（input要素とselect要素の両方に対応）
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('f-product__bundle-quantity-input') || 
          (e.target.classList.contains('f-product__bundle-variant-select') && 
           e.target.dataset.bundleProductHandle === 'shopping-bag')) {
        this.handleQuantityChange(e.target);
      }
    });
  }

  /**
   * チェックボックスの変更を処理
   */
  handleCheckboxChange(checkbox) {
    const productHandle = checkbox.dataset.bundleProductHandle;
    const variantSelect = document.getElementById(`bundle-variant-${productHandle}`);
    const quantityInput = document.getElementById(`bundle-quantity-${productHandle}`);

    if (checkbox.checked) {
      // チェックされた場合、選択肢と数量入力を有効化
      if (variantSelect) variantSelect.disabled = false;
      if (quantityInput) quantityInput.disabled = false;
    } else {
      // チェックが外された場合、選択肢と数量入力を無効化
      if (variantSelect) {
        variantSelect.disabled = true;
        variantSelect.value = '';
      }
      if (quantityInput) {
        quantityInput.disabled = true;
        quantityInput.value = '1';
      }
    }
  }

  /**
   * バリアント選択の変更を処理
   */
  handleVariantChange(select) {
    const productHandle = select.dataset.bundleProductHandle;
    const checkbox = document.querySelector(`[data-bundle-product-handle="${productHandle}"]`);
    
    // バリアントが選択された場合、チェックボックスを自動的にチェック
    if (select.value && checkbox) {
      checkbox.checked = true;
      this.handleCheckboxChange(checkbox);
    }
  }

  /**
   * 数量入力の変更を処理
   */
  handleQuantityChange(input) {
    const value = parseInt(input.value);
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 99;

    // ショッピングバッグの場合は0を許可
    if (input.dataset.bundleProductHandle === 'shopping-bag') {
      // select要素の場合は値の制限は不要（optionで制御済み）
      return;
    } else {
      // その他の商品は最小値・最大値の制限を適用
      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }

  /**
   * 選択されたバンドル商品の情報を取得
   */
  getSelectedBundleItems() {
    const selectedItems = [];
    const checkboxes = document.querySelectorAll('.f-product__bundle-add-checkbox:checked');

    checkboxes.forEach(checkbox => {
      const productHandle = checkbox.dataset.bundleProductHandle;
      const variantSelect = document.getElementById(`bundle-variant-${productHandle}`);
      const quantityInput = document.getElementById(`bundle-quantity-${productHandle}`);

      if (variantSelect && variantSelect.value && quantityInput) {
        const quantity = parseInt(quantityInput.value) || 0;
        
        // 数量が0より大きい場合のみカートに追加
        if (quantity > 0) {
          selectedItems.push({
            productHandle: productHandle,
            variantId: variantSelect.value,
            quantity: quantity
          });
        }
      }
    });

    return selectedItems;
  }

  /**
   * バンドル商品をカートに追加
   */
  async addBundleToCart() {
    const selectedItems = this.getSelectedBundleItems();
    if (selectedItems.length === 0) {
      console.log('バンドル商品が選択されていません');
      return;
    }

    // 追加前にカートの中身を取得
    let cart = null;
    try {
      const cartRes = await fetch('/cart.js');
      cart = await cartRes.json();
      console.log('[DEBUG] カート取得直後:', cart.items.map(i => ({handle: i.handle, variant_id: i.variant_id, title: i.title, quantity: i.quantity})));
    } catch (e) {
      console.error('カート情報の取得に失敗:', e);
    }

    const forceSingleHandles = ['wrapping', 'message-card'];
    // まず削除対象を集める
    const itemsToRemove = {};
    if (cart && cart.items) {
      for (const item of cart.items) {
        if (forceSingleHandles.includes(item.handle)) {
          itemsToRemove[item.variant_id] = 0;
        }
      }
    }
    console.log('[DEBUG] 削除対象 itemsToRemove:', itemsToRemove);

    try {
      // 1. まず削除リクエスト
      if (Object.keys(itemsToRemove).length > 0) {
        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ updates: itemsToRemove })
        });
        const removeJson = await removeRes.json();
        console.log('[DEBUG] 削除リクエスト後のレスポンス:', removeJson);
        // 削除反映待ち
        await new Promise(resolve => setTimeout(resolve, 700));
        // 削除後のカート内容を再取得
        const afterRemoveRes = await fetch('/cart.js');
        const afterRemoveCart = await afterRemoveRes.json();
        console.log('[DEBUG] 削除後カート:', afterRemoveCart.items.map(i => ({handle: i.handle, variant_id: i.variant_id, title: i.title, quantity: i.quantity})));
      }
      // 2. 追加リクエスト
      const cartItems = [];
      for (const item of selectedItems) {
        let quantity = item.quantity;
        if (forceSingleHandles.includes(item.productHandle)) {
          quantity = 1;
        }
        cartItems.push({ id: item.variantId, quantity });
      }
      console.log('[DEBUG] 追加リクエスト items:', cartItems);
      await this.addToCart(cartItems);
      // 追加後のカート内容を再取得
      const afterAddRes = await fetch('/cart.js');
      const afterAddCart = await afterAddRes.json();
      console.log('[DEBUG] 追加後カート:', afterAddCart.items.map(i => ({handle: i.handle, variant_id: i.variant_id, title: i.title, quantity: i.quantity})));
    } catch (error) {
      console.error('バンドル商品の追加に失敗しました:', error);
      this.showErrorMessage('バンドル商品の追加に失敗しました');
    }
  }

  /**
   * 商品ハンドルから商品データを取得
   */
  async getProductByHandle(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`商品データの取得に失敗しました (${productHandle}):`, error);
      return null;
    }
  }

  /**
   * カートに商品を追加
   */
  async addToCart(items) {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript'
      },
      body: JSON.stringify({ items })
    };

    try {
      const response = await fetch('/cart/add.js', config);
      const result = await response.json();

      if (response.ok) {
        // 成功時の処理
        this.showSuccessMessage();
        this.updateCartDrawer();
      } else {
        // エラー時の処理
        this.showErrorMessage(result.description || '商品の追加に失敗しました');
      }
    } catch (error) {
      console.error('カートへの追加に失敗しました:', error);
      this.showErrorMessage('ネットワークエラーが発生しました');
    }
  }

  /**
   * 成功メッセージを表示
   */
  showSuccessMessage() {
    // 既存の通知システムを使用
    if (window.FoxTheme && window.FoxTheme.Notification) {
      window.FoxTheme.Notification.show({
        target: document.body,
        method: 'appendChild',
        type: 'success',
        message: 'バンドル商品をカートに追加しました',
        last: 3000
      });
    }
  }

  /**
   * エラーメッセージを表示
   */
  showErrorMessage(message) {
    if (window.FoxTheme && window.FoxTheme.Notification) {
      window.FoxTheme.Notification.show({
        target: document.body,
        method: 'appendChild',
        type: 'warning',
        message: message,
        last: 3000
      });
    }
  }

  /**
   * カートドロワーを更新
   */
  updateCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      // カートの状態を更新
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartDrawer.renderContents(cart);
        })
        .catch(error => {
          console.error('カートの更新に失敗しました:', error);
        });
    }
  }
}

// ページ読み込み時に初期化
document.addEventListener('DOMContentLoaded', () => {
  new BundleProducts();
});

// グローバルに公開（必要に応じて）
window.BundleProducts = BundleProducts; 